import * as React from 'react';
import classNames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var PaginationContext = /*#__PURE__*/React.createContext({
  currentPage: 0,
  lastPage: 0
});
var usePagination = function usePagination() {
  return React.useContext(PaginationContext);
};
var PaginationContainer = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      currentPage = _ref.currentPage,
      lastPage = _ref.lastPage,
      _ref$ariaLabelledby = _ref['aria-labelledby'],
      ariaLabelledBy = _ref$ariaLabelledby === void 0 ? 'pagination-heading' : _ref$ariaLabelledby,
      _ref$ariaLabel = _ref['aria-label'],
      ariaLabel = _ref$ariaLabel === void 0 ? 'Resultatsider' : _ref$ariaLabel,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "className", "currentPage", "lastPage", "aria-labelledby", "aria-label"]);

  if (!Number.isInteger(currentPage)) {
    throw new TypeError("Invalid currentPage: " + JSON.stringify(currentPage));
  }

  if (!Number.isInteger(lastPage)) {
    throw new TypeError("Invalid lastPage: " + JSON.stringify(lastPage));
  }

  var context = React.useMemo(function () {
    return {
      currentPage: Math.max(1, Math.min(currentPage, lastPage)),
      lastPage: Math.max(1, lastPage)
    };
  }, [currentPage, lastPage]);
  return /*#__PURE__*/React.createElement("nav", _extends({}, props, {
    className: classNames('pagination', className),
    "aria-labelledby": ariaLabelledBy,
    ref: ref
  }), /*#__PURE__*/React.createElement("h1", {
    className: "u-screen-reader-only",
    id: ariaLabelledBy
  }, ariaLabel), /*#__PURE__*/React.createElement(PaginationContext.Provider, {
    value: context
  }, children));
});
PaginationContainer.displayName = "PaginationContainer";

var CurrentPage = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  var _usePagination = usePagination(),
      currentPage = _usePagination.currentPage;

  if (currentPage <= 1) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('u-hide-gt768 u-pa8', className),
    ref: ref
  }), children || "Side " + currentPage);
});
CurrentPage.displayName = "CurrentPage";

var FirstPage = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["className"]);

  var _usePagination = usePagination(),
      currentPage = _usePagination.currentPage;

  if (currentPage <= 2) {
    return null;
  }

  return /*#__PURE__*/React.createElement("a", _extends({
    "aria-label": "F\xF8rste resultatside"
  }, props, {
    ref: ref,
    className: classNames('button button--pill icon icon--pagination-first u-mr32', className),
    rel: "start"
  }));
});
FirstPage.displayName = "FirstPage";

var _span;
var NextPage = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? 'Neste' : _ref$children,
      className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  var _usePagination = usePagination(),
      currentPage = _usePagination.currentPage,
      lastPage = _usePagination.lastPage;

  if (currentPage >= lastPage) {
    return null;
  }

  return /*#__PURE__*/React.createElement("a", _extends({
    "aria-label": "Neste resultatside"
  }, props, {
    ref: ref,
    rel: "next nofollow",
    className: classNames('button button--pill button--has-icon button--icon-right', className)
  }), _span || (_span = /*#__PURE__*/React.createElement("span", {
    className: "icon icon--chevron-right"
  })), currentPage <= 1 && /*#__PURE__*/React.createElement("span", {
    className: "button__text u-hide-gt768"
  }, children));
});
NextPage.displayName = "NextPage";

var Page = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _ref$page = _ref.page,
      page = _ref$page === void 0 ? 0 : _ref$page,
      className = _ref.className,
      currentPage = _ref.currentPage,
      props = _objectWithoutPropertiesLoose(_ref, ["page", "className", "currentPage"]);

  if (page < 1) {
    return null;
  }

  return /*#__PURE__*/React.createElement("a", _extends({
    "aria-label": "Side " + page
  }, props, {
    ref: ref,
    rel: "nofollow",
    "aria-current": page === currentPage ? 'page' : undefined,
    className: classNames('pagination__page button button--pill', className)
  }), page);
});
Page.displayName = "Page";

var Pages = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);

  var _usePagination = usePagination(),
      currentPage = _usePagination.currentPage,
      lastPage = _usePagination.lastPage;

  var from = Math.max(currentPage - 3, 0);
  var to = Math.min(from + 7, lastPage);
  var pages = Array.from(Array(to - from).keys()).map(function (_, i) {
    return from + i + 1;
  });
  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('u-hide-lt768', className),
    ref: ref
  }), pages.map(function (page) {
    return /*#__PURE__*/React.cloneElement(children(page), {
      key: page,
      page: page,
      currentPage: currentPage
    });
  }));
});
Pages.displayName = "Pages";

var PrevPage = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      props = _objectWithoutPropertiesLoose(_ref, ["className"]);

  var _usePagination = usePagination(),
      currentPage = _usePagination.currentPage;

  if (currentPage <= 1) {
    return null;
  }

  return /*#__PURE__*/React.createElement("a", _extends({
    "aria-label": "Forrige resultatside"
  }, props, {
    ref: ref,
    className: classNames('button button--pill icon icon--chevron-left', className),
    rel: "prev nofollow"
  }));
});
PrevPage.displayName = "PrevPage";

var _CurrentPage;
var Pagination = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var currentPage = _ref.currentPage,
      lastPage = _ref.lastPage,
      createHref = _ref.createHref,
      className = _ref.className,
      onChange = _ref.onChange,
      props = _objectWithoutPropertiesLoose(_ref, ["currentPage", "lastPage", "createHref", "className", "onChange"]);

  if (!createHref) {
    throw new TypeError('createHref is undefined');
  }

  var handleClick = function handleClick(page) {
    return function (event) {
      if (onChange) {
        event.preventDefault();
        onChange(page);
      }
    };
  };

  return /*#__PURE__*/React.createElement(PaginationContainer, _extends({}, props, {
    ref: ref,
    currentPage: currentPage,
    lastPage: lastPage,
    className: className
  }), /*#__PURE__*/React.createElement(FirstPage, {
    href: createHref(1),
    onClick: handleClick(1)
  }), /*#__PURE__*/React.createElement(PrevPage, {
    href: createHref(currentPage - 1),
    onClick: handleClick(currentPage - 1)
  }), /*#__PURE__*/React.createElement(Pages, null, function (page) {
    return /*#__PURE__*/React.createElement(Page, {
      href: createHref(page),
      onClick: handleClick(page)
    });
  }), _CurrentPage || (_CurrentPage = /*#__PURE__*/React.createElement(CurrentPage, null)), /*#__PURE__*/React.createElement(NextPage, {
    href: createHref(currentPage + 1),
    onClick: handleClick(currentPage + 1)
  }));
});
Pagination.displayName = "Pagination";

export { CurrentPage, FirstPage, NextPage, Page, Pages, Pagination, PaginationContainer, PrevPage, usePagination };
//# sourceMappingURL=index.es.js.map
